import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import { LoadingScreen } from "@/components/LoadingScreen";
import { CategoryChip } from "@/components/CategoryChip";
import { EmptyState } from "@/components/EmptyState";
import { CenteredContainer } from "@/components/CenteredContainer";
import { documentTitleArticle } from "@/constants/app";
import { hr } from "@/constants/strings.hr";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useAppSelector } from "@/store";
import { useNotification } from "@/context/NotificationContext";
import { extractErrorMessage } from "@/api/axios";
import * as articlesApi from "@/api/articles.api";
import { useArticleDetail } from "@/hooks/useArticleDetail";
import { ArticleContent } from "./ArticleContent";
import { ReactionBar } from "./ReactionBar";
import { CommentSection } from "./CommentSection";

export function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { article, loading, error } = useArticleDetail(id);
  const navigate = useNavigate();
  const { user } = useAppSelector((s) => s.auth);
  const { notify } = useNotification();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isAdmin = Boolean(user?.isAdmin);

  useDocumentTitle(article ? documentTitleArticle(article.title) : undefined);

  if (loading) return <LoadingScreen />;

  if (error || !article) {
    return <EmptyState title={hr.article.notFound} titleVariant="h5" />;
  }

  const formattedDate = new Date(article.createdAt).toLocaleDateString(
    "hr-HR",
    { day: "numeric", month: "long", year: "numeric" },
  );

  const handleDeleteArticle = async () => {
    if (!id) return;
    try {
      await articlesApi.deleteArticle(id);
      notify(hr.article.articleDeleted, "success");
      setDeleteOpen(false);
      navigate("/");
    } catch (e) {
      notify(extractErrorMessage(e), "error");
    }
  };

  return (
    <CenteredContainer>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <CategoryChip name={article.category.name} />
        {isAdmin && (
          <Button
            color="error"
            size="small"
            variant="outlined"
            onClick={() => setDeleteOpen(true)}
          >
            {hr.article.deleteArticle}
          </Button>
        )}
      </Box>
      <Typography variant="h3" sx={{ mt: 2, mb: 1, lineHeight: 1.2 }}>
        {article.title}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          color: "text.secondary",
          mb: 2,
          fontWeight: 400,
          lineHeight: 1.4,
        }}
      >
        {article.subtitle}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          {hr.article.byline}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          |
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {formattedDate}
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />
      <ArticleContent html={article.content} />
      <Divider sx={{ my: 2 }} />
      <ReactionBar articleId={article.id} />
      <CommentSection articleId={article.id} />
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{hr.article.deleteArticleDialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {hr.article.deleteArticleDialogBody}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>
            {hr.profile.cancel}
          </Button>
          <Button
            onClick={handleDeleteArticle}
            color="error"
            variant="contained"
          >
            {hr.profile.confirmDelete}
          </Button>
        </DialogActions>
      </Dialog>
    </CenteredContainer>
  );
}
